import {
  BadgePill,
  CircleLoader,
  Colors,
  LoaderLooks,
  LoaderSizes,
  PillSize,
  StandardTooltip
} from '@brandfolder/react';
import { t, Trans } from '@lingui/macro';
import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import {
  PeopleTaggingStatus
} from '@api/v4/private/people_tagging/faces';
import { usePeopleTaggingFaces } from '@components/asset/modal/tabs/shared/hooks/usePeopleTaggingFaces';
import { resolvePeopleTaggingHostProductArea } from '@components/bulk_management/people_management/peopleHubAmplitude';
import { PeopleTaggingBackfillState } from '@components/people_tags/customPeopleTagTypes';

import { amplitudeService } from '../../../../../../services/amplitude';

import { FaceOverlay } from './FaceOverlay';
import { BoundingBox } from './types';

const PeopleSection = styled.div``;

const SectionTitle = styled.span<{ $marginBottom?: string }>`
  display: block;
  color: #4a4a4a;
  font-size: 14px;
  font-weight: 700;
  padding-top: 15px;
  text-transform: uppercase;
  font-family: "Circular-Medium", "Arial", "Trebuchet MS", sans-serif;
  margin-bottom: ${({ $marginBottom = '12px' }): string => $marginBottom};
`;

const PeopleGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
`;

const PersonItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 80px;
`;

const PersonImage = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 4px;
  object-fit: cover;
  margin-bottom: 6px;
`;

const PersonName = styled.span`
  font-size: 12px;
  color: #4a4a4a;
  text-align: center;
  max-width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PersonContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

// const EmptyStateMessage = styled.div`
//   color: #9b9b9b;
//   font-size: 14px;
//   line-height: 26px;
// `;

const LoaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .identifying-faces-loader {
    width: unset;
  }
`;

const IdentifyingFacesText = styled.span`
  color: #727682;
`;

const StatusBadgeContainer = styled.div`
  margin-top: 4px;
`;

const STATUS_BADGE_CONFIG: Record<string, { bg: string; color: string; label: () => string }> = {
  [PeopleTaggingBackfillState.Scanning]: {
    bg: '#D0F7EF',
    color: '#1C806B',
    label: () => t`Scanning`,
  },
  [PeopleTaggingBackfillState.Delayed]: {
    bg: '#FCEEB5',
    color: '#944A12',
    label: () => t`Scan delayed`,
  },
  [PeopleTaggingBackfillState.Scheduled]: {
    bg: '#EBECF0',
    color: '#4E5363',
    label: () => t`Scan scheduled`,
  },
  [PeopleTaggingBackfillState.NotStarted]: {
    bg: '#EBECF0',
    color: '#4E5363',
    label: () => t`Scan scheduled`,
  },
};

interface Person {
  id: string;
  name: string;
  imageUrl: string;
  assetCount: number;
  boundingBox: BoundingBox;
  isHidden: boolean;
  identity_id: string;
}

export interface PeopleInAssetProps {
  assetKey?: string;
  attachmentKey?: string;
}

export const PeopleInAsset: FunctionComponent<PeopleInAssetProps> = ({
  assetKey = undefined,
  attachmentKey = undefined,
}) => {
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const [people, setPeople] = useState<Person[]>([]);
  const [hoveredPerson, setHoveredPerson] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [overlayContainer, setOverlayContainer] = useState<HTMLDivElement | null>(null);
  const facesDetectedViewTrackedRef = useRef<string | null>(null);

  const brandfolderKey = BFG?.brandfolder_key;

  const backfillState: PeopleTaggingBackfillState =
    BFG.brandfolderSettings?.people_tagging_ui_state ?? PeopleTaggingBackfillState.Ready;
  const isBackfillReady = backfillState === PeopleTaggingBackfillState.Ready;

  // Only poll for faces when backfilling is complete
  const { faces: facesResponse, isLoading, status } = usePeopleTaggingFaces({
    attachmentKey,
    brandfolderKey,
    enabled: !!attachmentKey && isBackfillReady
  });

  useEffect(() => {
    facesDetectedViewTrackedRef.current = null;
  }, [assetKey, attachmentKey]);

  useEffect(() => {
    if (!assetKey || isLoading || facesResponse.length === 0) {
      return;
    }
    if (!BFG.context.hasFeature('people_tagging') || !amplitudeService.isReady()) {
      return;
    }
    const dedupeKey = `${assetKey}:${attachmentKey ?? ''}`;
    if (facesDetectedViewTrackedRef.current === dedupeKey) {
      return;
    }
    facesDetectedViewTrackedRef.current = dedupeKey;

    const detectedFaces = facesResponse.length;
    const namedFaces = facesResponse.filter((f) => (f.identity_name || '').trim() !== '').length;
    const unnamedFaces = detectedFaces - namedFaces;

    const eventArea = 'AppFrontend';
    const eventName = 'PageLoad';
    const productArea = resolvePeopleTaggingHostProductArea();
    const featureArea = 'AssetModal';
    const elementName = 'FacesDetectedView';
    const elementLocation = 'FaceOverlay';
    const eventFull = `${eventArea}.${productArea}.${featureArea}.${elementName}.${eventName}`;
    amplitudeService.trackEvent(eventName, {
      eventArea,
      productArea,
      featureArea,
      elementLocation,
      elementName,
      eventName,
      eventFull,
      brandfolderId: BFG?.brandfolder_key ?? undefined,
      organizationId: BFG?.organization_key ?? undefined,
      userId: BFG?.currentUser?.user_id ?? undefined,
      userRole: BFG?.currentUser?.role ?? undefined,
      assetId: assetKey,
      detectedFaces,
      namedFaces,
      unnamedFaces,
    });
  }, [assetKey, attachmentKey, isLoading, facesResponse]);

  const handleImageError = (personId: string): void => {
    setImageErrors((prev) => ({
      ...prev,
      [personId]: true
    }));
  };

  const handlePersonHover = useCallback((personId: string): void => {
    setHoveredPerson(personId);
  }, []);

  const handlePersonLeave = useCallback((): void => {
    setHoveredPerson(null);
  }, []);

  // Transform faces response to Person format
  useEffect(() => {
    if (!facesResponse || facesResponse.length === 0) {
      setPeople([]);
      return;
    }

    const mapped: Person[] = facesResponse.map((f) => {

      const isHidden = JSON.parse(f.identity_metadata || '{}')?.hide || false;

      return {
        id: f.id,
        name: f.identity_name || '', // placeholder until name is resolved
        imageUrl: f.identity_thumbnail_url || f.face_thumbnail_url,
        assetCount: f.face_count || 0,
        isHidden,
        identity_id: f.identity_id,
        boundingBox: {
          height: f.bounding_box.height,
          left: f.bounding_box.left,
          top: f.bounding_box.top,
          width: f.bounding_box.width
        }
      };
    });

    setPeople(mapped);
  }, [facesResponse]);

  // Wait for image to load and create overlay container
  useEffect(() => {
    // Reset state when attachment changes
    setImageLoaded(false);
    setOverlayContainer(null);

    const setupOverlayContainer = (): void => {
      const imageElement = document.getElementById('asset-preview-image') as HTMLImageElement | null;
      const wrapperElement = document.getElementById('asset-preview-wrapper');

      if (!imageElement || !wrapperElement) {
        setImageLoaded(false);
        setOverlayContainer(null);
        return;
      }

      // Check if image is loaded and has dimensions
      if (!imageElement.complete || imageElement.naturalWidth === 0 || imageElement.naturalHeight === 0) {
        setImageLoaded(false);
        setOverlayContainer(null);
        return;
      }

      // Get image's bounding rect relative to wrapper
      const imageRect = imageElement.getBoundingClientRect();
      const wrapperRect = wrapperElement.getBoundingClientRect();

      // Calculate position relative to wrapper
      const left = imageRect.left - wrapperRect.left;
      const top = imageRect.top - wrapperRect.top;
      const width = imageRect.width;
      const height = imageRect.height;

      // Create or update overlay container
      let container = document.getElementById('face-overlay-container') as HTMLDivElement | null;

      if (!container) {
        container = document.createElement('div');
        container.id = 'face-overlay-container';
        container.style.position = 'absolute';
        // Container is transparent and only used for positioning
        // Overlays handle their own pointer events via CSS
        wrapperElement.appendChild(container);
      }

      // Update container position and size to match image
      container.style.left = `${left}px`;
      container.style.top = `${top}px`;
      container.style.width = `${width}px`;
      container.style.height = `${height}px`;

      setOverlayContainer(container);
      setImageLoaded(true);
    };

    // Try to setup immediately if image is already loaded
    setupOverlayContainer();

    // Listen for image load event
    const imageElement = document.getElementById('asset-preview-image') as HTMLImageElement | null;
    if (imageElement && !imageElement.complete) {
      const handleImageLoad = (): void => {
        setupOverlayContainer();
      };

      imageElement.addEventListener('load', handleImageLoad);

      return (): void => {
        imageElement.removeEventListener('load', handleImageLoad);
      };
    }

    // Also listen for window resize to update container position
    const handleResize = (): void => {
      setupOverlayContainer();
    };

    window.addEventListener('resize', handleResize);

    return (): void => {
      window.removeEventListener('resize', handleResize);
      // Clean up container on unmount
      const container = document.getElementById('face-overlay-container');
      if (container) {
        container.remove();
      }
    };
  }, [attachmentKey, people.length]); // Re-run when attachment changes or people are loaded

  const handlePersonClick = useCallback(
    ({ personName, personIdentityId }: { personName?: string; personIdentityId: string }): void => {
      const searchValue = personName || personIdentityId;
      const searchQuery = `people.strict:("${searchValue}")`;
      const encodedQuery = encodeURIComponent(searchQuery);
      if (personIdentityId) {
        sessionStorage.setItem('people_search_person_id', personIdentityId);
      }
      window.open(`${window.location.pathname}?q=${encodedQuery}`, '_blank');
    },
    []
  );

  const handleFaceOverlayClick = useCallback(
    (e: React.MouseEvent, person: Person): void => {
      e.stopPropagation();
      if (BFG.context.hasFeature('people_tagging') && amplitudeService.isReady() && assetKey) {
        const eventArea = 'AppFrontend';
        const eventName = 'ButtonClick';
        const productArea = resolvePeopleTaggingHostProductArea();
        const featureArea = 'AssetModal';
        const elementName = 'FaceBoundingBox';
        const elementLocation = 'FaceOverlay';
        const eventFull = `${eventArea}.${productArea}.${featureArea}.${elementName}.${eventName}`;
        const isNamed = (person.name || '').trim() !== '';
        amplitudeService.trackEvent(eventName, {
          eventArea,
          productArea,
          featureArea,
          elementLocation,
          elementName,
          eventName,
          eventFull,
          brandfolderId: BFG?.brandfolder_key ?? undefined,
          organizationId: BFG?.organization_key ?? undefined,
          userId: BFG?.currentUser?.user_id ?? undefined,
          userRole: BFG?.currentUser?.role ?? undefined,
          assetId: assetKey,
          identityId: person.identity_id || null,
          isNamed,
        });
      }
      if (person.identity_id) {
        handlePersonClick({ personName: person.name, personIdentityId: person.identity_id });
      }
    },
    [assetKey, handlePersonClick]
  );

  const faceOverlaysPortal = useMemo(() => {
    if (!overlayContainer || !imageLoaded || people.length === 0) {
      return null;
    }

    return createPortal(
      <>
        {people.map((person) => (
          <FaceOverlay
            key={person.id}
            boundingBox={person.boundingBox}
            isVisible={hoveredPerson === person.id}
            onFaceClick={(e): void => handleFaceOverlayClick(e, person)}
            onHover={() => handlePersonHover(person.id)}
            onLeave={handlePersonLeave}
            personName={person.isHidden ? '' : person.name}
          />
        ))}
      </>,
      overlayContainer
    );
  }, [
    hoveredPerson, handleFaceOverlayClick, handlePersonHover,
    handlePersonLeave, people, overlayContainer, imageLoaded
  ]);

  const badgeConfig = STATUS_BADGE_CONFIG[backfillState];
  const isPolling = status === PeopleTaggingStatus.WaitingForPeopleTagging;
  const showLoader = isBackfillReady && (isLoading || isPolling);
  const onlyVisiblePeople = people.filter((person) => !person.isHidden);
  const noFacesDetected = isBackfillReady && !showLoader && people.length === 0;
  const showPeople = isBackfillReady && !showLoader && onlyVisiblePeople.length > 0;

  if (noFacesDetected) {
    return null;
  }

  return (
    <PeopleSection>
      <SectionTitle>
        <Trans>People in Asset</Trans>
      </SectionTitle>

      {!isBackfillReady && badgeConfig && (
        <StatusBadgeContainer>
          <BadgePill
            backgroundColor={badgeConfig.bg}
            fontColor={badgeConfig.color}
            fontSize={12}
            radius={4}
            size={PillSize.XSmall}
          >
            <strong>{badgeConfig.label()}</strong>
          </BadgePill>
        </StatusBadgeContainer>
      )}

      {showLoader && (
        <LoaderContainer>
          <CircleLoader
            className='identifying-faces-loader'
            label={isPolling ? t`Identifying faces...` : t`Loading...`}
            look={LoaderLooks.Gray}
            size={LoaderSizes.Small}
          />
          <IdentifyingFacesText>
            {isPolling ? <Trans>Identifying faces...</Trans> : <Trans>Loading...</Trans>}
          </IdentifyingFacesText>
        </LoaderContainer>
      )}

      {/* {noFacesDetected && (
        <EmptyStateMessage>
          <Trans>No faces detected</Trans>
        </EmptyStateMessage>
      )} */}
      {showPeople && (
        <PeopleGrid>
          {onlyVisiblePeople.map((person) => {
            const isClickable = !!person.identity_id;

            const personContent = (
              <PersonContent
                onClick={
                  isClickable
                    ? (): void => handlePersonClick({ personName:person.name, personIdentityId:person.identity_id })
                    : undefined
                }
                onMouseEnter={() => handlePersonHover(person.id)}
                onMouseLeave={handlePersonLeave}
                style={{ cursor: isClickable ? 'pointer' : 'default' }}
              >
                <PersonImage
                  alt={`${person.name}`}
                  data-testid="person-thumbnail"
                  onError={() => handleImageError(person.id)}
                  src={imageErrors[person.id]
                    ? `${BFG?.icon_service_url || ''}/assets/types/person.svg`
                    : person.imageUrl}
                />
                <PersonName>
                  {person.name}
                </PersonName>
              </PersonContent>
            );

            const tooltipText = t`View ${person.assetCount} ${person.assetCount === 1 ? 'asset' : 'assets'}`;

            return (
              <PersonItem key={person.id}>
                {isClickable ? (
                  <StandardTooltip
                    color={Colors.None}
                    id={`person-tooltip-${person.id}`}
                    tooltip={tooltipText}
                  >
                    {personContent}
                  </StandardTooltip>
                ) : (
                  personContent
                )}
              </PersonItem>
            );
          })}
        </PeopleGrid>
      )}

      {faceOverlaysPortal}
    </PeopleSection>
  );
};
